/**
 * SurveyDocuments — Document upload page with type classification.
 * Route: /visits/:visitId/survey/documents
 */

import { useState, useEffect, useContext } from 'react'
import { useParams, Link } from 'react-router-dom'
import { ArrowLeft, FileText, CheckCircle, Flag, ExternalLink } from 'lucide-react'
import { AreaSelector } from '../../components/survey/AreaSelector'
import { DocumentScanner } from '../../components/survey/DocumentScanner'
import { SyncStatus } from '../../components/survey/SyncStatus'
import { FieldAppShell } from '../../components/survey/FieldAppShell'
import { AuthContext } from '../../context/AuthContext'
import { surveyStore, type PendingDocument } from '../../lib/offline/surveyStore'
import { getPrestudyDocumentDownloadUrl, getPrestudyDocuments, updateDocumentStatus, type PrestudyDocument } from '../../lib/services/prestudy'
import type { FacilityArea, SurveySession } from '../../lib/services/survey'

export function SurveyDocuments() {
  const { visitId } = useParams<{ visitId: string }>()
  const authContext = useContext(AuthContext)
  const [session, setSession] = useState<SurveySession | null>(null)
  const [area, setArea] = useState<FacilityArea | null>(null)
  const [documents, setDocuments] = useState<PendingDocument[]>([])
  const [prestudyDocs, setPrestudyDocs] = useState<PrestudyDocument[]>([])
  const [openingDocumentId, setOpeningDocumentId] = useState<string | null>(null)
  const [documentActionError, setDocumentActionError] = useState<{ documentId: string; message: string } | null>(null)
  const isFieldSurveyor = authContext?.viewerContext?.role === 'FieldSurveyor'
  const backTo = isFieldSurveyor ? '/today' : '..'
  const backLabel = isFieldSurveyor ? 'Back to Today' : 'Back'

  useEffect(() => {
    if (!visitId) return
    surveyStore.getSessionByVisit(visitId).then((s) => setSession(s ?? null))
    // Load pre-survey documents from API
    getPrestudyDocuments(visitId).then(setPrestudyDocs).catch(() => {})
  }, [visitId])

  useEffect(() => {
    if (!session) return
    surveyStore.getDocumentsBySurvey(session.survey_id).then(setDocuments)
  }, [session])

  async function handleOpenPrestudyDocument(documentId: string) {
    if (!visitId) return
    setDocumentActionError(null)
    setOpeningDocumentId(documentId)
    try {
      const downloadUrl = await getPrestudyDocumentDownloadUrl(visitId, documentId)
      window.open(downloadUrl, '_blank', 'noopener,noreferrer')
    } catch {
      setDocumentActionError({ documentId, message: 'Failed to open document. Try again.' })
    } finally {
      setOpeningDocumentId(null)
    }
  }

  if (!visitId) {
    return (
      <FieldAppShell eyebrow="Survey" title="Documents" backTo={backTo} backLabel={backLabel}>
        <div className="px-4 py-6">
          <p className="text-sm text-slate-500">No active survey.</p>
          <Link to={backTo} className="btn-ghost text-sm mt-4 inline-block">{backLabel}</Link>
        </div>
      </FieldAppShell>
    )
  }

  return (
    <FieldAppShell eyebrow="Survey" title="Documents" backTo={backTo} backLabel={backLabel} rightSlot={<SyncStatus />}>
    <div className="space-y-4 px-4 py-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Link to={backTo} className="text-slate-400 hover:text-slate-600" aria-label={backLabel}>
            <ArrowLeft size={20} />
          </Link>
          <h2 className="section-title">Documents</h2>
        </div>
        <SyncStatus />
      </div>

      {session ? (
        <>
          <div className="app-surface p-4 space-y-3">
            <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Area (optional)</span>
            <AreaSelector value={area} onChange={setArea} />
          </div>

          <div className="app-surface p-4">
            <DocumentScanner
              visitId={visitId}
              surveyId={session.survey_id}
              area={area}
              onCapture={(doc) => setDocuments((prev) => [doc, ...prev])}
            />
          </div>
        </>
      ) : (
        <div className="app-surface p-4 space-y-2">
          <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Pre-Survey Documents</span>
          <p className="text-sm text-slate-600">Review uploaded facility documents before starting the survey.</p>
        </div>
      )}

      {/* Pre-survey documents (from facility portal) */}
      {prestudyDocs.length > 0 && (
        <div className="space-y-2">
          <span className="text-xs font-bold uppercase tracking-wider text-slate-500 px-1">Pre-Survey Documents</span>
          {prestudyDocs.map((doc) => (
            <div key={doc.document_id} className="app-surface p-3 space-y-2">
              <div className="flex items-center gap-3">
                <FileText size={20} className="text-blue-500 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-slate-700 truncate">{doc.label}</p>
                  <p className="text-xs text-slate-400">
                    {doc.doc_type} — uploaded {new Date(doc.created_at).toLocaleDateString()}
                    {doc.uploaded_by_name && ` by ${doc.uploaded_by_name}`}
                  </p>
                </div>
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                  doc.status === 'reviewed' ? 'bg-emerald-100 text-emerald-700' :
                  doc.status === 'flagged' ? 'bg-red-100 text-red-700' :
                  'bg-amber-100 text-amber-700'
                }`}>
                  {doc.status}
                </span>
              </div>
              {doc.file_path ? (
                <button
                  type="button"
                  onClick={() => {
                    void handleOpenPrestudyDocument(doc.document_id)
                  }}
                  disabled={openingDocumentId === doc.document_id}
                  className="inline-flex items-center gap-1 self-start rounded-lg border border-slate-200 px-2.5 py-1.5 text-[11px] font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <ExternalLink size={12} />
                  {openingDocumentId === doc.document_id ? 'Opening…' : 'Open document'}
                </button>
              ) : null}
              {doc.status === 'pending' || doc.status === 'uploaded' ? (
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      updateDocumentStatus(visitId, doc.document_id, { status: 'reviewed' })
                        .then((updated) => setPrestudyDocs((prev) => prev.map((d) => d.document_id === updated.document_id ? updated : d)))
                    }}
                    className="flex-1 py-1.5 rounded-lg text-[11px] font-bold bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                  >
                    <CheckCircle size={12} className="inline mr-1" />
                    Mark Reviewed
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      const notes = prompt('Flag reason:')
                      if (notes) {
                        updateDocumentStatus(visitId, doc.document_id, { status: 'flagged', reviewer_notes: notes })
                          .then((updated) => setPrestudyDocs((prev) => prev.map((d) => d.document_id === updated.document_id ? updated : d)))
                      }
                    }}
                    className="flex-1 py-1.5 rounded-lg text-[11px] font-bold bg-red-50 text-red-600 hover:bg-red-100"
                  >
                    <Flag size={12} className="inline mr-1" />
                    Flag Issue
                  </button>
                </div>
              ) : doc.reviewer_notes ? (
                <p className="text-[10px] text-slate-500 italic">Note: {doc.reviewer_notes}</p>
              ) : null}
              {documentActionError?.documentId === doc.document_id ? (
                <p className="text-[10px] text-red-600">{documentActionError.message}</p>
              ) : null}
            </div>
          ))}
        </div>
      )}

      {/* Upload pre-survey document */}
      <div className="app-surface p-4 space-y-3">
        <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Upload Pre-Survey Document</span>
        <p className="text-[10px] text-slate-400">Upload policies, logs, credentials, or other facility documents for pre-survey review.</p>
        <Link
          to={`/visits/${visitId}/prestudy/upload`}
          className="btn-primary inline-flex items-center gap-2 text-sm py-2 px-4"
        >
          <FileText size={14} />
          Upload Document
        </Link>
      </div>

      {/* Captured documents */}
      {session && (
        <div className="space-y-2">
          {documents.length > 0 && (
            <span className="text-xs font-bold uppercase tracking-wider text-slate-500 px-1">Captured On-Site</span>
          )}
          {documents.map((doc) => (
            <div key={doc.document_id} className="app-surface p-3 flex items-center gap-3">
              <FileText size={20} className="text-[var(--brand-primary)] flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-slate-700 truncate">{doc.label}</p>
                <p className="text-xs text-slate-400">
                  {doc.doc_type} {doc.area ? `— ${doc.area}` : ''}
                </p>
              </div>
              <span className="brand-chip text-[10px]">
                {doc.blob ? 'Queued' : 'Synced'}
              </span>
            </div>
          ))}
          {documents.length === 0 && prestudyDocs.length === 0 && (
            <p className="text-center text-sm text-slate-400 py-8">No documents captured yet.</p>
          )}
        </div>
      )}
      {!session && prestudyDocs.length === 0 && (
        <div className="app-surface p-4 text-center text-sm text-slate-400">
          No uploaded facility documents yet.
        </div>
      )}
    </div>
    </FieldAppShell>
  )
}
